import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCampaigns } from '../hooks/useCampaigns';
import { useToast } from '../contexts/ToastContext';
import CampaignForm from '../components/campaigns/CampaignForm';
import { CampaignCreate } from '../types/campaign.types';

const CreateCampaignPage: React.FC = () => {
    const navigate = useNavigate();
    const { showToast } = useToast();
    const { createCampaign } = useCampaigns();
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (data: CampaignCreate) => {
        try {
            setLoading(true);
            const campaign = await createCampaign(data);

            if (campaign) {
                showToast('Campaign created successfully!', 'success');
                navigate(`/campaigns/${campaign.id}`);
            } else {
                showToast('Failed to create campaign', 'error');
            }
        } catch (err) {
            showToast('An error occurred while creating the campaign', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        navigate('/campaigns');
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="mb-8">
                    <button
                        onClick={() => navigate('/campaigns')}
                        className="flex items-center text-blue-600 hover:text-blue-700 mb-4"
                    >
                        <svg
                            className="w-5 h-5 mr-2"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M15 19l-7-7 7-7"
                            />
                        </svg>
                        Back to Campaigns
                    </button>
                    <h1 className="text-3xl font-bold text-gray-900">Create New Campaign</h1>
                    <p className="text-gray-600 mt-1">
                        Set up a new SMS campaign to reach your audience
                    </p>
                </div>

                {/* Campaign Form */}
                <div className="bg-white rounded-lg shadow-sm p-6">
                    <CampaignForm
                        onSubmit={handleSubmit}
                        onCancel={handleCancel}
                        loading={loading}
                    />
                </div>

                {/* Help Section */}
                <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-blue-900 mb-3">
                        Next Steps
                    </h3>
                    <ol className="list-decimal list-inside space-y-2 text-blue-800">
                        <li>Create your campaign with a name and message template</li>
                        <li>Upload contacts via Excel/CSV file</li>
                        <li>Review your contacts and message preview</li>
                        <li>Execute the campaign to send SMS to all contacts</li>
                    </ol>
                </div>
            </div>
        </div>
    );
};

export default CreateCampaignPage;